import { Typography } from '@mui/material';
import {
  AssessmentsGradeUpdate,
  GET_ASSESSMENT_GRADES_FOR_SCHOOL_QUERY,
  useArchiveAssessmentsGradeMutation,
  useCreateAssessmentsGradeMutation,
  useGetAssessmentGradeQuery,
  useUpdateAssessmentsGradeMutation,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { useConfirmationDialog } from '@schooly/components/confirmation-dialog';
import { useNotifications } from '@schooly/components/notifications';
import { useFlag } from '@schooly/hooks/use-flag';
import { Loading } from '@schooly/style';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { ModalSmall } from '../../../components/uikit-components/Modal/Modal.styled';
import { useRouter } from '../../../context/router/useRouter';
import { SchoolPropertyArchiveConfirmationModal } from '../SchoolPropertyArchiveConfirmationModal';
import {
  AssessmentsGradeForm,
  SchoolAssessmentsGradeModalContent,
} from './SchoolAssessmentsGradeModalContent';
import { convertGradeFormToGradeUpdate } from './utils';

export const SchoolAssessmentsGradeModal: FC = () => {
  const { formatMessage } = useIntl();

  const { schoolId = '' } = useAuth();
  const { id: gradeId = '' } = useParams<'id'>();
  const { showNotification, showError } = useNotifications();
  const { getConfirmation } = useConfirmationDialog();
  const { goBack } = useRouter();
  const queryClient = useQueryClient();
  const [
    isArchiveConfirmationModalOpen,
    showArchiveConfirmationModal,
    hideArchiveConfirmationModal,
  ] = useFlag();
  const archiveAssessmentsGrade = useArchiveAssessmentsGradeMutation();
  const updateAssessmentsGrade = useUpdateAssessmentsGradeMutation();
  const createAssessmentsGrade = useCreateAssessmentsGradeMutation();

  const closeModal = goBack;

  const { data: grade, isFetching } = useGetAssessmentGradeQuery(gradeId, {
    enabled: !!gradeId,
    refetchOnMount: 'always',
  });

  const handleSave = useCallback(
    async (data: AssessmentsGradeForm) => {
      if (!schoolId) return;

      const updatedGrade: AssessmentsGradeUpdate = convertGradeFormToGradeUpdate(data);
      const name = updatedGrade.name;

      const confirmed = grade?.options.some((o) => o.has_assessments)
        ? await getConfirmation({
            textId: 'assessments-GradeUpdateConfirm',
            textValues: { name: `"${name}"` },
            content: (
              <Typography variant="h3" mt={2.5}>
                {formatMessage({ id: 'assessments-GradeUpdateConfirm-Description' })}
              </Typography>
            ),
          })
        : true;

      if (!confirmed) return;

      const onSuccess = () => {
        showNotification({
          textId: gradeId ? 'assessments-GradeUpdated' : 'assessments-GradeSaved',
          values: { gradeName: name },
          type: 'success',
        });
        queryClient.invalidateQueries([GET_ASSESSMENT_GRADES_FOR_SCHOOL_QUERY]);
        goBack();
      };

      if (gradeId) {
        updateAssessmentsGrade.mutate(
          { listId: gradeId, grade: updatedGrade },
          { onSuccess, onError: showError },
        );

        return;
      }

      createAssessmentsGrade.mutate(
        { schoolId, grade: updatedGrade },
        { onSuccess, onError: showError },
      );
    },
    [
      createAssessmentsGrade,
      formatMessage,
      getConfirmation,
      goBack,
      grade?.options,
      gradeId,
      queryClient,
      schoolId,
      showError,
      showNotification,
      updateAssessmentsGrade,
    ],
  );

  const handleArchive = useCallback(async () => {
    if (!grade?.name) return;

    archiveAssessmentsGrade.mutate(gradeId, {
      onError: showError,
      onSuccess: () => {
        showNotification({
          textId: 'assessments-GradeArchived',
          values: { gradeName: grade.name },
          type: 'success',
        });

        queryClient.invalidateQueries([GET_ASSESSMENT_GRADES_FOR_SCHOOL_QUERY]);
        closeModal();
      },
    });
  }, [
    archiveAssessmentsGrade,
    closeModal,
    grade?.name,
    gradeId,
    queryClient,
    showError,
    showNotification,
  ]);

  if (isFetching) {
    return (
      <ModalSmall open onClose={closeModal}>
        <Loading />
      </ModalSmall>
    );
  }

  return (
    <>
      <SchoolAssessmentsGradeModalContent
        grade={grade}
        isNewItem={!gradeId}
        isArchiving={archiveAssessmentsGrade.isLoading}
        onClose={closeModal}
        isSaving={createAssessmentsGrade.isLoading || updateAssessmentsGrade.isLoading}
        onArchiveClick={gradeId ? showArchiveConfirmationModal : undefined}
        onSubmit={handleSave}
      />

      {!!grade && (
        <SchoolPropertyArchiveConfirmationModal
          open={isArchiveConfirmationModalOpen}
          title={formatMessage(
            { id: 'assessments-GradeArchiveConfirmAction' },
            { gradeName: grade.name },
          )}
          message={<FormattedMessage id="assessments-GradeArchiveConfirmActionInformation" />}
          onClose={hideArchiveConfirmationModal}
          onArchive={handleArchive}
          isArchiving={archiveAssessmentsGrade.isLoading}
        />
      )}
    </>
  );
};
